import { useState } from "react";
import { toast } from "sonner";
import { Download, ExternalLink } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Notice } from "@/components/Badges";
import { fmtDateTime, STATUS_LABELS } from "@/lib/format";
import TrackingPanel from "@/components/application/TrackingPanel";

const METHODS = { portal_handoff: "Guided handoff to the official portal", adviser_handoff: "Handoff to an authorised adviser", direct_api: "Direct submission via verified integration" };
const ATT_STYLE = { in_progress: "text-indigo-700", uncertain: "text-amber-700", failed: "text-red-700", submitted: "text-teal-700" };

const OutcomeForm = ({ app, att, reload }) => {
  const [f, setF] = useState({ outcome: "submitted", reference: "", submitted_at: "", acknowledgement_text: "", note: "" });
  const [file, setFile] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("receipt", file);
    try {
      await api.post(`/applications/${app.id}/submission/${att.id}/outcome`, fd);
      toast.success("Outcome recorded");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="mt-3 grid gap-3 rounded-md bg-slate-50 p-4 sm:grid-cols-2" data-testid="outcome-form">
      <select aria-label="Outcome" data-testid="outcome-select" className="input" value={f.outcome} onChange={set("outcome")}>
        <option value="submitted">Submitted - I have an official acknowledgement</option><option value="failed">Failed - not submitted</option><option value="uncertain">Uncertain - awaiting confirmation</option>
      </select>
      <input aria-label="Official reference" data-testid="outcome-reference-input" className="input" placeholder="Official reference / application number" value={f.reference} onChange={set("reference")} />
      <input aria-label="Submission time" type="datetime-local" data-testid="outcome-time-input" className="input" value={f.submitted_at} onChange={set("submitted_at")} />
      <input aria-label="Receipt file" type="file" data-testid="outcome-receipt-input" className="input" onChange={(e) => setFile(e.target.files[0])} />
      <textarea aria-label="Acknowledgement text" data-testid="outcome-ack-input" className="input sm:col-span-2" rows={2} placeholder="Paste the official acknowledgement text (if no receipt file)" value={f.acknowledgement_text} onChange={set("acknowledgement_text")} />
      <input aria-label="Note" className="input sm:col-span-2" placeholder="Note (e.g. error message if failed)" value={f.note} onChange={set("note")} />
      <button data-testid="outcome-submit-button" className="btn-teal btn-sm sm:w-fit">Record outcome</button>
    </form>
  );
};

export default function SubmissionPanel({ app, reload }) {
  const [method, setMethod] = useState("portal_handoff");
  const internal = app.template.submission_route === "internal_record";
  const start = async () => {
    try {
      await api.post(`/applications/${app.id}/submission/start`, { method });
      toast.success("Submission attempt started. Use the export pack on the official portal, then record the outcome.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const portal = app.opportunity?.application_url || app.template.official_portal_url;
  return (
    <div className="space-y-6">
      <section className="card p-6" data-testid="submission-route">
        <h2 className="font-semibold text-[#0B192C]">Submission route</h2>
        {internal ? <p className="mt-2 text-sm text-slate-700">Internal record: no official submission exists. Approve and export the record for your files.</p> : (
          <p className="mt-2 text-sm text-slate-700">No verified direct submission integration exists for this route. Export the pack and submit on the official portal{app.kind === "patent" ? " via a registered patent attorney" : ""}. Status changes to {app.kind === "grant" ? "Submitted" : "Filed"} only after you record the official acknowledgement.</p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => downloadFile(`/applications/${app.id}/export`, "application_pack.zip")} data-testid="export-pack-button" className="btn-ghost btn-sm"><Download className="h-4 w-4" />Export pack {app.approval_valid ? "(approved version)" : "(draft)"}</button>
          {portal && <a href={portal} target="_blank" rel="noreferrer" data-testid="official-portal-link" className="btn-ghost btn-sm">Official portal <ExternalLink className="h-4 w-4" /></a>}
        </div>
        {!internal && ["approved_for_submission", "ready_for_filing"].includes(app.status) && !app.attempts.some((a) => ["in_progress", "uncertain"].includes(a.status)) && (
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <select aria-label="Submission method" data-testid="submission-method-select" className="input sm:max-w-sm" value={method} onChange={(e) => setMethod(e.target.value)}>{Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <button onClick={start} data-testid="start-submission-button" className="btn-teal btn-sm">Start submission</button>
          </div>
        )}
        {!internal && ["draft", "awaiting_review"].includes(app.status) && app.attempts.length === 0 && <div className="mt-4"><Notice tone="info">Approve the exact version on the Review & approve tab before starting submission.</Notice></div>}
      </section>
      {app.attempts.length > 0 && (
        <section className="card p-6" data-testid="attempts-list">
          <h2 className="font-semibold text-[#0B192C]">Submission attempts</h2>
          <ul className="mt-3 space-y-4">
            {app.attempts.map((a) => (
              <li key={a.id} className="border-b border-slate-100 pb-3 text-sm" data-testid={`attempt-${a.status}`}>
                <p><b className={ATT_STYLE[a.status]}>{a.status.replace("_", " ").toUpperCase()}</b> · {METHODS[a.method]} · started {fmtDateTime(a.started_at)} by {a.by}</p>
                {a.reference && <p className="mt-1">Reference <span className="font-mono" data-testid="attempt-reference">{a.reference}</span> · submitted {fmtDateTime(a.submitted_at)}</p>}
                {a.receipt_filename && <button onClick={() => downloadFile(`/applications/${app.id}/attempts/${a.id}/receipt`, a.receipt_filename)} className="mt-1 text-xs text-[#0D9488] underline" data-testid="download-receipt">Download receipt</button>}
                {a.note && <p className="text-xs text-slate-500">{a.note}</p>}
                {["in_progress", "uncertain"].includes(a.status) && <OutcomeForm key={`${a.id}-${a.status}-${app.updated_at}`} app={app} att={a} reload={reload} />}
              </li>
            ))}
          </ul>
        </section>
      )}
      <TrackingPanel key={app.updated_at} app={app} reload={reload} />
      <section className="card p-6" data-testid="events-timeline">
        <h2 className="font-semibold text-[#0B192C]">History</h2>
        <ul className="mt-3 space-y-1 text-xs">{[...app.events].reverse().map((e, i) => <li key={i}><span className="font-mono text-slate-500">{fmtDateTime(e.at)}</span> · {e.by} · <b>{STATUS_LABELS[e.type] || e.type.replace(/_/g, " ")}</b> {e.note}</li>)}</ul>
      </section>
    </div>
  );
}
